// Libraries
import { Divider, Flex, Text } from '@chakra-ui/react';
import { FieldArray, FormikErrors } from 'formik';
import { Fragment } from 'react';
// Components
import { SliderInput } from '@molecules/index';
import { CiiSimulatorFormValues } from '@schemas/forms';
import { FuelMode } from '@schemas/services';
import { FuelInput } from './Components';
// Types
import { FuelModeGroupProps } from './FuelModeGroup.types';

/**
 * `FuelModeGroup` component is used to display and configure a group of fuel modes,
 * including a title, a SliderInput for configuring the percentage, and a list of FuelInput components
 * for configuring the costs of individual fuels within the mode.
 *
 * @component
 * @param {FuelModeGroupProps} props - The properties to configure the FuelModeGroup component.
 * @param {string} props.error - An error message, if any, related to the SliderInput component.
 * @param {FuelMode} props.fuelMode - The fuel mode information.
 * @param {number} props.index - The index of the fuel mode group.
 * @param {boolean} props.isDisabled - A flag indicating whether the component is disabled.
 * @param {boolean} props.isLoading - A flag indicating whether the component is in a loading state.
 * @param {string} props.label - The label for the SliderInput component.
 * @param {Function} props.onChange - A callback function triggered on SliderInput value change.
 * @param {string} props.title - The title of the fuel mode group.
 * @param {number} props.value - The value for the SliderInput component.
 *
 * @returns {ReactElement} The rendered FuelModeGroup component.
 */
export const FuelModeGroup = ({
  error,
  fuelMode,
  index,
  isDisabled,
  isLoading,
  label,
  onChange,
  title,
  value,
}: FuelModeGroupProps) => {
  const disableInputs = isDisabled || value === '0';
  const opacity = disableInputs ? 0.5 : 1;

  return (
    <Flex direction='column' w='full' minW={60}>
      <Text fontSize='m' color='text.base' fontWeight='semibold'>
        {fuelMode.mode}
      </Text>
      <SliderInput
        isLoading={isLoading}
        isDisabled={isDisabled}
        label={''}
        min={0}
        max={100}
        value={value}
        step={1}
        error={error}
        onChange={onChange}
        currency='%'
        currencyPosition='end'
        maxW={24}
        maxLength={3}
      />
      <Text fontSize='m' color='text.base' mt={1} mb={2}>
        {title}
      </Text>
      <Flex
        align='center'
        bg='table.footer'
        border='primaryPurple'
        borderRadius='lg'
        px={3}
        direction='column'
        opacity={opacity}
      >
        <FieldArray
          name={`fuelModes.[${index}].fuels`}
          render={({ form }) => {
            return (
              <>
                {(form.values as CiiSimulatorFormValues).fuelModes[index].fuels.map((fuel, idx) => {
                  const showDivider = idx < fuelMode.fuels.length - 1;
                  const fieldSelector = `fuelModes.[${index}].fuels.[${idx}].costMTUsd`;
                  return (
                    <Fragment key={fuel.name}>
                      <FuelInput
                        label={label}
                        onInputChange={(value?: string) => form.setFieldValue(fieldSelector, value)}
                        name={fuel.name}
                        displayName={fuel.displayName}
                        category={fuel.category}
                        isLoading={isLoading}
                        isDisabled={disableInputs}
                        inputValue={`${(form.values as CiiSimulatorFormValues).fuelModes[index].fuels[idx].costMTUsd}`}
                        error={
                          ((form.errors as FormikErrors<CiiSimulatorFormValues>).fuelModes?.[index] as FuelMode)
                            ?.fuels?.[idx]?.costMTUsd
                        }
                      />
                      {showDivider && <Divider borderBottomWidth='1px' borderColor='moonraker' />}
                    </Fragment>
                  );
                })}
              </>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};
